import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiFileText, FiEye, FiDownload } from 'react-icons/fi';
import { LoadingSpinner } from './LoadingSpinner';
import Toast, { ToastContainer } from './Toast';
import {
  getQuoteDocument,
  listAssignedDocumentTemplates,
  selectQuoteTemplate,
  saveQuoteDocument,
  previewQuoteDocument,
  documentErrorMessage,
  type AiDocumentTemplateOption,
  type SectorAiDocumentConfig,
} from '../services/SectorDocumentService';
import SessionService from '../services/SessionService';
import {
  fieldDefaultsFromDocument,
  mergeTemplateOptions,
  FALLBACK_DOCUMENT_FIELDS,
} from '../utils/aiDocumentUtils';

interface Props {
  sectorId: number;
  inboundAiAvailable: boolean;
}

interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

function fieldDefaults(doc: SectorAiDocumentConfig | null): Record<string, string> {
  return fieldDefaultsFromDocument(doc);
}

const DocumentsTab: React.FC<Props> = ({ sectorId, inboundAiAvailable }) => {
  const [doc, setDoc] = useState<SectorAiDocumentConfig | null>(null);
  const [templates, setTemplates] = useState<AiDocumentTemplateOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [mockFields, setMockFields] = useState<Record<string, string>>({});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastId = useRef(0);

  const addToast = (message: string, type: ToastItem['type'] = 'info') => {
    const id = ++toastId.current;
    setToasts(t => [...t, { id, message, type }]);
  };
  const removeToast = (id: number) => setToasts(t => t.filter(x => x.id !== id));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = SessionService.getToken();
      if (!token) return;
      const [d, assignedTemplates] = await Promise.all([
        getQuoteDocument(sectorId),
        listAssignedDocumentTemplates(),
      ]);
      let docConfig = d;
      if (d.status === 'ready' && !d.enabled) {
        try {
          docConfig = await saveQuoteDocument(sectorId, { enabled: true });
        } catch {
          /* keep loaded config */
        }
      }
      setDoc(docConfig);
      setTemplates(mergeTemplateOptions(assignedTemplates, docConfig));
      setSelectedTemplateId(docConfig.templateId ?? '');
      setMockFields(fieldDefaults(docConfig));
    } catch (e) {
      addToast(await documentErrorMessage(e, 'Não foi possível carregar os documentos.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [sectorId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (previewUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleSelectTemplate = async (templateId: number) => {
    setSelecting(true);
    try {
      const result = await selectQuoteTemplate(sectorId, templateId);
      setDoc(result);
      setSelectedTemplateId(result.templateId ?? templateId);
      setTemplates(mergeTemplateOptions(templates, result));
      setMockFields(fieldDefaults(result));
      addToast('Modelo de documento aplicado', 'success');
    } catch (e) {
      addToast(await documentErrorMessage(e, 'Não foi possível selecionar o modelo.'), 'error');
      setSelectedTemplateId(doc?.templateId ?? '');
    } finally {
      setSelecting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const blob = await previewQuoteDocument(sectorId, mockFields);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      addToast('Prévia gerada', 'success');
    } catch (e) {
      addToast(await documentErrorMessage(e, 'Não foi possível gerar a prévia.'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-documents-loading">
        <LoadingSpinner size="lg" color="primary" />
        <span>Carregando documentos...</span>
      </div>
    );
  }

  const isReady = doc?.status === 'ready';
  const hasTemplatePicker = templates.length > 0;
  const showNoTemplates = !hasTemplatePicker && !doc?.templateId;
  const previewFields = doc?.fields.length ? doc.fields : FALLBACK_DOCUMENT_FIELDS;

  return (
    <div className="ai-documents-tab">
      <ToastContainer>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
        ))}
      </ToastContainer>

      <div className="ai-documents-header">
        <div>
          <h2><FiFileText size={20} /> Documentos</h2>
          <p>Escolha o modelo de documento desta conexão. A IA preenche os campos e pode enviar o PDF pelo WhatsApp.</p>
        </div>
        <div className={`ai-documents-status ai-documents-status--${selecting ? 'processing' : doc?.status ?? 'empty'}`}>
          {selecting
            ? 'Aplicando modelo...'
            : isReady
              ? 'Modelo pronto'
              : doc?.status === 'error'
                ? 'Erro'
                : 'Nenhum modelo selecionado'}
        </div>
      </div>

      {!inboundAiAvailable && (
        <div className="ai-documents-receptive-banner" role="status">
          Os orçamentos em PDF são enviados quando um <strong>funcionário de IA ativo</strong> atende a conversa. Ative o atendimento por IA ou defina um funcionário de campanha, follow-up ou CRM que responda aos leads.
        </div>
      )}

      <section className="ai-hub-setting-card ai-documents-template-select">
        <div className="ai-hub-setting-card__info">
          <h5>1. Modelo de documento</h5>
          <p>Escolha qual modelo de PDF esta conexão usa nos orçamentos.</p>
        </div>
        {showNoTemplates ? (
          <div className="ai-documents-empty-templates">
            Ainda não há modelos de documento disponíveis para sua conta. Fale com o suporte se precisar de acesso.
          </div>
        ) : (
          <label className="ai-documents-template-picker">
            <span className="ai-documents-template-picker__label">Selecionar modelo</span>
            <div className="ai-documents-template-picker__control">
              <select
                value={selectedTemplateId}
                disabled={selecting}
                aria-busy={selecting}
                onChange={e => {
                  const val = e.target.value === '' ? '' : Number(e.target.value);
                  setSelectedTemplateId(val);
                  if (typeof val === 'number') void handleSelectTemplate(val);
                }}
              >
                <option value="">Escolha um documento...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {selecting && (
                <div className="ai-documents-template-picker__loading" role="status" aria-live="polite">
                  <LoadingSpinner size="sm" color="primary" />
                  <span>Aplicando...</span>
                </div>
              )}
            </div>
          </label>
        )}
        {doc?.templateName && isReady && (
          <p className="ai-documents-template-active">Ativo: <strong>{doc.templateName}</strong></p>
        )}
      </section>

      {doc?.status === 'error' && doc.processingError && (
        <div className="ai-documents-error">{doc.processingError}</div>
      )}

      {isReady && (
        <section className="ai-documents-preview">
          <h5>2. Prévia</h5>
          <div className="ai-documents-preview__grid">
            <div className="ai-documents-preview__form">
              {previewFields.map(f => (
                <label key={f.key} className="ai-documents-field">
                  <span>{f.label}</span>
                  <input
                    value={mockFields[f.key] ?? ''}
                    onChange={e => setMockFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </label>
              ))}
              <button type="button" className="ai-save-button ai-documents-preview__btn" onClick={() => void handlePreview()} disabled={previewing}>
                {previewing ? <LoadingSpinner size="sm" color="white" /> : <><FiEye size={15} /> Gerar prévia</>}
              </button>
            </div>
            <div className="ai-documents-preview__viewer">
              {previewUrl ? (
                <>
                  <iframe title="Prévia do PDF" src={previewUrl} />
                  <a href={previewUrl} download="quote-preview.pdf" className="ai-documents-download">
                    <FiDownload size={14} /> Baixar
                  </a>
                </>
              ) : (
                <div className="ai-documents-preview__empty">
                  Preencha os campos e clique em Gerar prévia
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default DocumentsTab;
